const MUD_TRAIL_CONFETTI_COLORS = ['#9b8afb', '#ffa560', '#ff9fcd', '#52d9d1'];
const MUD_TRAIL_DISMISS_KEY = 'mud-trail:dismissed';
const MUD_TRAIL_VISITED_KEY = 'mud-trail:visited';
const MUD_TRAIL_MEMORY_KEY = 'mud-trail:last-viewed';
const MUD_TRAIL_DISCOUNT_KEY = 'mud-trail:discount-revealed';
const MUD_TRAIL_CHAT_HISTORY_KEY = 'mud-trail:chat-history';
const MUD_TRAIL_INTRO_KEY = 'mud-trail:intro-played';
const MUD_TRAIL_IDLE_VARIANTS = ['is-idle-look', 'is-idle-stretch'];
// Share of the free space inside the eyeball a pupil may travel (the painted eyes have a thick clay rim)
const MUD_TRAIL_PUPIL_TRAVEL = 0.75;

class MudTrailMascot extends HTMLElement {
  connectedCallback() {
    if (this.wasDismissedThisSession()) {
      this.hidden = true;
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.hidden = true;
      return;
    }

    this.track = this.querySelector('[data-mud-trail-track]');
    this.figure = this.querySelector('[data-mud-trail-figure]');
    this.tiltLayer = this.querySelector('[data-mud-trail-tilt]');
    this.eyes = Array.from(this.querySelectorAll('[data-mud-trail-eye]'), (eye) => ({ eye, pupil: eye.querySelector('[data-mud-trail-pupil]') }));
    this.footprintLayer = this.querySelector('[data-mud-trail-footprints]');
    this.particleLayer = this.querySelector('[data-mud-trail-particles]');
    this.bubble = this.querySelector('[data-mud-trail-bubble]');
    this.introRope = this.querySelector('[data-mud-trail-intro-rope]');
    this.chatTemplate = this.querySelector('[data-mud-trail-chat]');
    this.dismissButton = this.querySelector('[data-mud-trail-dismiss]');
    this.chatEl = null;
    this.chatOpen = false;
    this.chatGreeted = false;
    this.typingEl = null;

    if (!this.track || !this.figure) return;

    this.pageType = this.getAttribute('page-type') || '';
    this.enableFootprints = this.hasAttribute('enable-footprints');
    this.enableTap = this.hasAttribute('enable-tap');
    this.enableSound = this.hasAttribute('enable-sound');
    this.enableExitIntent = this.hasAttribute('enable-exit-intent');
    this.enableMemory = this.hasAttribute('enable-memory');
    this.enableAnalytics = this.hasAttribute('enable-analytics');
    this.footprintSpacing = Number(this.getAttribute('footprint-spacing')) || 120;
    this.footprintLifetime = Number(this.getAttribute('footprint-lifetime')) || 2.5;
    this.exitMessage = this.getAttribute('exit-message') || "Wait, don't go! Let me know if you need help finding something.";
    this.introAnimation = this.getAttribute('intro-animation') || 'disabled';
    this.introPlaying = false;

    this.side = 'left';
    this.lastFootprintY = window.scrollY;
    this.ticking = false;
    this.tiltTicking = false;
    this.shownCallouts = new Set();
    this.lastTapTime = 0;
    this.isDragCandidate = false;
    this.isDragging = false;
    this.dragMoved = false;
    this.dragPointerId = null;
    this.dragTicking = false;
    this.totalTaps = 0;
    this.idleVariantIndex = 0;
    this.lastActivity = Date.now();
    this.hasGreeted = false;
    this.hasShownExitIntent = false;
    this.wasHidden = false;
    this.rememberedProduct = null;
    this.petLastX = null;
    this.petLastDirection = null;
    this.petReversals = 0;
    this.petLastReversalTime = 0;
    this.petLastTriggered = 0;

    const config = this.querySelector('[data-mud-trail-config]');
    try {
      this.callouts = config ? JSON.parse(config.textContent) : [];
    } catch (error) {
      this.callouts = [];
    }

    const chatConfigEl = this.querySelector('[data-mud-trail-chat-config]');
    try {
      this.chatConfig = chatConfigEl ? JSON.parse(chatConfigEl.textContent) : null;
    } catch (error) {
      this.chatConfig = null;
    }

    this.cartItemCount = (this.chatConfig && this.chatConfig.cartItemCount) || 0;

    if (this.enableMemory) {
      this.applyMemory();
    }

    this.callouts.sort((a, b) => a.position - b.position);

    this.introPlaying = Boolean(this.introRope) && this.shouldPlayIntro();

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();

    if (this.enableTap) {
      this.onTap = this.onTap.bind(this);
      this.onFigureKeydown = this.onFigureKeydown.bind(this);
      this.onOutsideClick = this.onOutsideClick.bind(this);
      this.onFigureMouseMove = this.onFigureMouseMove.bind(this);
      this.onFigurePointerDown = this.onFigurePointerDown.bind(this);
      this.onFigurePointerMove = this.onFigurePointerMove.bind(this);
      this.onFigurePointerUp = this.onFigurePointerUp.bind(this);
      this.figure.addEventListener('click', this.onTap);
      this.figure.addEventListener('keydown', this.onFigureKeydown);
      this.figure.addEventListener('mousemove', this.onFigureMouseMove);
      this.figure.addEventListener('pointerdown', this.onFigurePointerDown);
      this.figure.addEventListener('pointermove', this.onFigurePointerMove);
      this.figure.addEventListener('pointerup', this.onFigurePointerUp);
      this.figure.addEventListener('pointercancel', this.onFigurePointerUp);
    }

    if (this.dismissButton) {
      this.dismissButton.addEventListener('click', () => this.dismiss());
    }

    this.onMouseMove = this.onMouseMove.bind(this);
    window.addEventListener('mousemove', this.onMouseMove, { passive: true });

    // Eyes follow a finger too (passive: never blocks scrolling)
    this.onTouchLook = this.onTouchLook.bind(this);
    window.addEventListener('touchstart', this.onTouchLook, { passive: true });
    window.addEventListener('touchmove', this.onTouchLook, { passive: true });

    this.onCartChange = this.onCartChange.bind(this);
    document.addEventListener('cart:change', this.onCartChange);

    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if (this.enableExitIntent) {
      this.onMouseLeaveDoc = this.onMouseLeaveDoc.bind(this);
      document.addEventListener('mouseleave', this.onMouseLeaveDoc);
    }

    this.startBlinking();
    this.startIdleWatcher();
    this.startPersonalityLoop();

    if (this.introPlaying) {
      this.playIntroAnimation();
    } else {
      this.scheduleGreeting();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('touchstart', this.onTouchLook);
    window.removeEventListener('touchmove', this.onTouchLook);
    document.removeEventListener('click', this.onOutsideClick, true);
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('mouseleave', this.onMouseLeaveDoc);
    clearTimeout(this.walkTimeout);
    clearTimeout(this.bubbleTimeout);
    clearTimeout(this.blinkTimeout);
    clearTimeout(this.blinkResetTimeout);
    clearTimeout(this.greetingTimeout);
    clearTimeout(this.idleWaveResetTimeout);
    clearTimeout(this.idleVariantResetTimeout);
    clearTimeout(this.singleTapTimeout);
    clearInterval(this.idleInterval);
    clearInterval(this.personalityInterval);
  }

  wasDismissedThisSession() {
    try {
      return sessionStorage.getItem(MUD_TRAIL_DISMISS_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  dismiss() {
    try {
      sessionStorage.setItem(MUD_TRAIL_DISMISS_KEY, '1');
    } catch (error) {
      // Storage unavailable — the mascot will just reappear on the next page, which is an acceptable fallback
    }
    this.dispatchAnalyticsEvent('dismiss');
    this.remove();
  }

  onScroll() {
    this.lastActivity = Date.now();

    if (!this.ticking) {
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    this.figure.classList.add('is-walking');
    clearTimeout(this.walkTimeout);
    this.walkTimeout = setTimeout(() => {
      this.figure.classList.remove('is-walking');
    }, 220);

    if (this.enableFootprints && Math.abs(window.scrollY - this.lastFootprintY) >= this.footprintSpacing) {
      this.dropFootprint();
      this.lastFootprintY = window.scrollY;
    }
  }

  getProgress() {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    return max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
  }

  update() {
    const progress = this.getProgress();

    if (!this.isDragging && !this.introPlaying) {
      const trackHeight = this.track.clientHeight;
      const figureHeight = this.figure.clientHeight || 64;
      this.figure.style.top = `${progress * Math.max(0, trackHeight - figureHeight)}px`;
    }

    this.checkCallouts(progress);
  }

  dropFootprint() {
    if (!this.footprintLayer) return;

    this.side = this.side === 'left' ? 'right' : 'left';
    const progress = this.getProgress();
    const trackHeight = this.track.clientHeight;

    const footprint = document.createElement('div');
    footprint.className = `mud-trail-mascot__footprint mud-trail-mascot__footprint--${this.side}`;
    footprint.style.top = `${progress * trackHeight}px`;
    footprint.style.animationDuration = `${this.footprintLifetime}s`;
    footprint.innerHTML = '<svg viewBox="0 0 24 34" width="16" height="22" aria-hidden="true"><ellipse cx="12" cy="20" rx="8" ry="12" fill="#8a5a34"/><circle cx="7" cy="6" r="3" fill="#8a5a34"/><circle cx="12" cy="4" r="3.2" fill="#8a5a34"/><circle cx="17" cy="6" r="3" fill="#8a5a34"/></svg>';
    footprint.addEventListener('animationend', () => footprint.remove());
    this.footprintLayer.appendChild(footprint);
  }

  checkCallouts(progress) {
    if (!this.bubble || !this.callouts.length) return;

    const percent = progress * 100;
    const reached = this.callouts.filter(
      (callout) => !this.shownCallouts.has(callout.position) && percent >= callout.position
    );

    if (!reached.length) return;

    const next = reached.reduce((latest, callout) => (callout.position > latest.position ? callout : latest));
    this.callouts.forEach((callout) => {
      if (callout.position <= next.position) this.shownCallouts.add(callout.position);
    });
    this.showBubble(next.message);
  }

  showBubble(message, duration = 3500) {
    if (!this.bubble) return;

    this.bubble.textContent = message;
    this.bubble.classList.add('is-visible');
    clearTimeout(this.bubbleTimeout);
    this.bubbleTimeout = setTimeout(() => {
      this.bubble.classList.remove('is-visible');
    }, duration);
  }

  onFigureKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onTap();
    }
  }

  onFigureMouseMove(event) {
    this.lastActivity = Date.now();

    if (this.isDragging || this.isDragCandidate) return;

    const x = event.clientX;
    if (this.petLastX === null) {
      this.petLastX = x;
      return;
    }

    const delta = x - this.petLastX;
    if (Math.abs(delta) < 6) return;

    const direction = delta > 0 ? 1 : -1;
    this.petLastX = x;

    const now = Date.now();
    if (this.petLastReversalTime && now - this.petLastReversalTime > 900) {
      this.petReversals = 0;
    }

    if (this.petLastDirection !== null && direction !== this.petLastDirection) {
      this.petReversals += 1;
      this.petLastReversalTime = now;
    }
    this.petLastDirection = direction;

    if (this.petReversals >= 3 && now - this.petLastTriggered > 1800) {
      this.petLastTriggered = now;
      this.petReversals = 0;
      this.triggerPetting();
    }
  }

  triggerPetting() {
    this.figure.classList.remove('is-petted');
    void this.figure.offsetWidth;
    this.figure.classList.add('is-petted');

    this.spawnHearts();
    this.playPurrSound();

    const messages = ['Aww, that feels nice!', 'Hehe, right there!', "You're the best!", 'Purr... 💕'];
    this.showBubble(messages[Math.floor(Math.random() * messages.length)], 2200);
    this.dispatchAnalyticsEvent('petted');
  }

  spawnHearts() {
    if (!this.particleLayer) return;
    const hearts = ['💗', '💕', '💖'];

    for (let i = 0; i < 3; i++) {
      const heart = document.createElement('span');
      heart.className = 'mud-trail-mascot__heart';
      heart.textContent = hearts[Math.floor(Math.random() * hearts.length)];
      heart.style.setProperty('--mud-trail-heart-x', `${(Math.random() - 0.5) * 40}px`);
      heart.style.animationDuration = `${1 + Math.random() * 0.4}s`;
      heart.style.animationDelay = `${i * 0.12}s`;
      heart.addEventListener('animationend', () => heart.remove());
      this.particleLayer.appendChild(heart);
    }
  }

  /* ---------------------------------------------------------------------
   * Drag to pick up
   * ------------------------------------------------------------------- */

  onFigurePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.isDragging) return;

    this.isDragCandidate = true;
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.lastDragX = event.clientX;
  }

  onFigurePointerMove(event) {
    if (!this.isDragCandidate || event.pointerId !== this.dragPointerId) return;

    const dx = event.clientX - this.dragStartX;
    const dy = event.clientY - this.dragStartY;

    if (!this.isDragging && Math.hypot(dx, dy) > 8) {
      this.startDragging(event);
    }

    if (this.isDragging) {
      event.preventDefault();
      this.updateDragPosition(event.clientX, event.clientY);
    }
  }

  onFigurePointerUp(event) {
    if (event.pointerId !== this.dragPointerId) return;

    this.isDragCandidate = false;
    this.dragPointerId = null;

    if (this.isDragging) {
      this.endDragging();
    }
  }

  startDragging(event) {
    // Cancel any pending cleanup from a previous drag that hasn't finished returning yet, so it
    // can't wipe out this new drag's inline styles once its fallback timer fires
    if (this.dragCleanupFn) {
      this.figure.removeEventListener('transitionend', this.dragCleanupFn);
      clearTimeout(this.dragCleanupTimeout);
      this.dragCleanupFn = null;
    }

    this.isDragging = true;
    this.dragMoved = true;

    try {
      this.figure.setPointerCapture(this.dragPointerId);
    } catch (error) {
      // Pointer capture is a nice-to-have for smoother dragging — safe to skip if unsupported
    }

    this.figure.classList.add('is-dragging');

    // Read a FRESH rect right now (not one captured earlier at pointerdown) and derive the grab
    // offset from it, so any movement between the initial touch and crossing the drag threshold
    // (e.g. from a scroll or the idle-bob animation) can't cause a jump when dragging begins
    const rect = this.figure.getBoundingClientRect();
    this.dragGrabOffsetX = event.clientX - rect.left;
    this.dragGrabOffsetY = event.clientY - rect.top;

    this.figure.style.width = `${rect.width}px`;
    this.figure.style.height = `${rect.height}px`;
    this.figure.style.position = 'fixed';
    this.figure.style.left = `${rect.left}px`;
    this.figure.style.top = `${rect.top}px`;
    this.figure.style.transform = 'none';
    this.figure.style.transition = 'none';

    const messages = ['Wheee!', 'Hey! 😲', 'Put me down!', 'Whoaaa!'];
    this.showBubble(messages[Math.floor(Math.random() * messages.length)], 4000);
    this.dispatchAnalyticsEvent('drag-start');
  }

  updateDragPosition(clientX, clientY) {
    if (this.dragTicking) return;
    this.dragTicking = true;

    requestAnimationFrame(() => {
      const x = clientX - this.dragGrabOffsetX;
      const y = clientY - this.dragGrabOffsetY;
      this.figure.style.left = `${x}px`;
      this.figure.style.top = `${y}px`;

      const velocityX = clientX - this.lastDragX;
      this.lastDragX = clientX;
      const wobble = Math.max(-14, Math.min(14, velocityX * 1.5));
      if (this.tiltLayer) this.tiltLayer.style.setProperty('--mud-trail-tilt', `${wobble}deg`);

      this.dragTicking = false;
    });
  }

  getNaturalFigurePosition() {
    const trackRect = this.track.getBoundingClientRect();
    const progress = this.getProgress();
    const figureHeight = this.figure.offsetHeight || 64;
    const figureWidth = this.figure.offsetWidth || 64;

    return {
      left: trackRect.left + (trackRect.width - figureWidth) / 2,
      top: trackRect.top + progress * Math.max(0, trackRect.height - figureHeight),
    };
  }

  endDragging() {
    this.isDragging = false;
    this.figure.classList.remove('is-dragging');

    try {
      this.figure.releasePointerCapture(this.dragPointerId);
    } catch (error) {
      // No-op if capture was never established
    }

    const target = this.getNaturalFigurePosition();

    this.figure.style.transition = 'left 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), top 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)';
    this.figure.style.left = `${target.left}px`;
    this.figure.style.top = `${target.top}px`;
    if (this.tiltLayer) this.tiltLayer.style.setProperty('--mud-trail-tilt', '0deg');

    this.showBubble('Phew! 😊', 1500);
    this.dispatchAnalyticsEvent('drag-end');

    const cleanup = () => {
      this.figure.style.position = '';
      this.figure.style.left = '';
      this.figure.style.top = '';
      this.figure.style.width = '';
      this.figure.style.height = '';
      this.figure.style.transform = '';
      this.figure.style.transition = '';
      this.figure.removeEventListener('transitionend', cleanup);
      clearTimeout(this.dragCleanupTimeout);
      this.dragCleanupFn = null;
    };
    this.dragCleanupFn = cleanup;
    this.figure.addEventListener('transitionend', cleanup);
    // Fallback in case the drop position exactly matches the resting position — then left/top
    // never actually change, transitionend never fires, and cleanup would otherwise never run
    this.dragCleanupTimeout = setTimeout(cleanup, 700);

    // Touch suppresses the synthetic click after a dragged pointer sequence, so onTap (which
    // normally resets dragMoved) may never fire — reset it here so the next real tap isn't swallowed
    setTimeout(() => {
      this.dragMoved = false;
    }, 400);
  }

  shouldPlayIntro() {
    if (this.introAnimation === 'disabled') return false;

    if (this.introAnimation === 'once_per_session') {
      try {
        if (sessionStorage.getItem(MUD_TRAIL_INTRO_KEY)) return false;
      } catch (error) {
        // Storage unavailable — treat every load as a first play, which is a harmless fallback
      }
    }

    return true;
  }

  playIntroAnimation() {
    // Defer measurement by a frame so mobile browsers that are still settling viewport/chrome
    // dimensions on first paint (e.g. collapsing address bar) don't get measured mid-shift
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.runIntroAnimation());
    });
  }

  runIntroAnimation() {
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const announcementHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--announcement-bar-height')) || 0;
    const pivotX = window.innerWidth / 2;
    // The logo sits roughly at the header's vertical midpoint, not its bottom edge
    const pivotY = announcementHeight + headerHeight / 2;

    const rect = this.figure.getBoundingClientRect();
    const target = this.getNaturalFigurePosition();

    // Rope length = straight-line distance from the pivot (near the header center) to where the
    // mascot actually rests — this is what makes the string length adapt to device size.
    const targetCenterX = target.left + rect.width / 2;
    const dx = targetCenterX - pivotX;
    const dy = target.top - pivotY;
    const ropeLength = Math.max(24, Math.sqrt(dx * dx + dy * dy));
    // CSS rotate() is clockwise-positive; rotating the "hangs straight down" vector (0,1) by θ
    // gives (-sinθ, cosθ), which points right (+x) for NEGATIVE θ — hence the negation here.
    const trueAngleDeg = -Math.atan2(dx, dy) * (180 / Math.PI);

    if (!Number.isFinite(ropeLength) || !Number.isFinite(trueAngleDeg)) {
      this.introPlaying = false;
      this.scheduleGreeting();
      return;
    }

    // The rail sits close vertically to the header but far to the side, so the angle that lands
    // exactly on it is nearly horizontal (~75-85° on most layouts) — technically correct but looks
    // like the mascot lying flat rather than swinging. Cap it to a steep, pendulum-like angle
    // instead, and close the remaining gap with one fast snap right at the end (see finishIntro).
    // 65° (not a smaller "prettier" angle like 40°) matters here: the gap between the capped arc's
    // endpoint and the true resting spot grows fast as the cap moves away from trueAngleDeg — at
    // 40° it was over 1000px on a typical desktop layout, which read as a teleport rather than a
    // snap. 65° keeps the swing clearly steep while shrinking that gap to something a fast
    // transition can sell as a settle.
    const maxSwingAngle = 65;
    const settleAngleDeg = Math.max(-maxSwingAngle, Math.min(maxSwingAngle, trueAngleDeg));

    // Cap the swing's overshoot to a fixed on-screen arc distance rather than a fixed angle —
    // otherwise a long rope (common on wide desktop screens, since the rail sits far to the side)
    // would translate a fixed angle into a huge, potentially off-screen sweep.
    const maxArcPx = 90;
    const overshootMagnitude = Math.min(55, Math.max(10, (maxArcPx / ropeLength) * (180 / Math.PI)));
    const overshootDeg = settleAngleDeg >= 0 ? overshootMagnitude : -overshootMagnitude;

    this.figure.classList.add('is-intro');

    if (this.introRope) {
      this.introRope.style.height = `${ropeLength}px`;
      this.introRope.classList.add('is-visible');
    }

    this.figure.style.width = `${rect.width}px`;
    this.figure.style.height = `${rect.height + ropeLength}px`;
    this.figure.style.position = 'fixed';
    this.figure.style.left = `${pivotX}px`;
    this.figure.style.top = `${pivotY}px`;

    try {
      sessionStorage.setItem(MUD_TRAIL_INTRO_KEY, '1');
    } catch (error) {
      // Storage unavailable — the intro may just replay next load, which is an acceptable fallback
    }

    this.dispatchAnalyticsEvent('intro-start');

    // Driven by the Web Animations API with plain computed numbers rather than a CSS class +
    // custom-property + calc() combo — the latter turned out unreliable (setting a custom property
    // and starting the class-driven animation in the same tick could leave the animation never
    // actually running in some cases, permanently stranding the mascot mid-swing with the rope
    // stuck visible). animate() also gives a real .finished promise instead of depending on an
    // animationend DOM event that may not fire.
    //
    // The pivot stays at the logo, but the mascot itself should read as starting from the far
    // side of the header (near the top-left corner of the navbar) and swinging all the way
    // across, through vertical, over to its resting side — a full pendulum arc, not a small
    // swing that only covers the distance from vertical to the resting angle. That means the
    // starting angle is on the OPPOSITE side of vertical from where it settles, with roughly the
    // same steepness as the settle cap so the arc looks symmetric swinging in from the far side.
    const introStartAngleDeg = settleAngleDeg < 0 ? maxSwingAngle : -maxSwingAngle;
    //
    // A single aggregate easing across the whole sequence (as a previous version used) put a
    // heavily front-loaded curve on top of ALL five keyframes at once — since that curve reached
    // ~90% of its total progress within the first 20% of the duration, the entire dramatic sweep
    // from the start angle to the overshoot peak visually completed within the first ~400ms of an
    // 1800ms animation, leaving over a second of nearly-imperceptible damping wobble afterward.
    // From the outside that reads as "it's already swung by the time I notice it, then does
    // something slow after" rather than a visible swing. Giving each keyframe its own easing
    // (WAAPI applies a keyframe's easing to the segment arriving at it) spreads the big sweep
    // evenly across its own time slice instead of cramming it into the very start.
    // A brief static hold at the start angle before the sweep starts — the whole animation only
    // takes ~1.5s, so without this a viewer whose eyes land on the page even slightly after it
    // fires (normal reaction time to a reload) simply misses the start entirely and only catches
    // the animation already mid-swing, which reads as "it started already swung." The hold gives
    // a clear, motionless beat to register the mascot hanging from the logo before it moves — the
    // classic animation "anticipation" principle.
    // The final resting rotation is trueAngleDeg (the exact angle that lands on the target), not
    // the capped settleAngleDeg — landing the ROTATION itself exactly on target means no separate
    // corrective motion is needed afterward. A previous version settled the rotation at the
    // capped angle and then slid the mascot the rest of the way with a plain left/top transition;
    // that second motion was a different *kind* of movement (a straight-line slide) stitched onto
    // the end of a rotation, which read as a disconnected "swipe" rather than a continuation of
    // the swing's own momentum. Blending the damping bounces' center of oscillation from
    // settleAngleDeg (steep, for a dramatic-looking peak) toward trueAngleDeg (exact landing) lets
    // the last few keyframes arrive at the true angle as one continuous decaying motion.
    const overshootPeakDeg = settleAngleDeg + overshootDeg;
    const blendToTrueAngle = (t) => settleAngleDeg + (trueAngleDeg - settleAngleDeg) * t;
    const keyframes = [
      { transform: `translateX(-50%) rotate(${introStartAngleDeg}deg)` },
      { transform: `translateX(-50%) rotate(${introStartAngleDeg}deg)`, offset: 0.14 },
      {
        transform: `translateX(-50%) rotate(${overshootPeakDeg}deg)`,
        offset: 0.62,
        easing: 'cubic-bezier(0.45, 0, 0.55, 1)',
      },
      {
        transform: `translateX(-50%) rotate(${blendToTrueAngle(0.3) - overshootDeg * 0.3}deg)`,
        offset: 0.78,
        easing: 'ease-out',
      },
      {
        transform: `translateX(-50%) rotate(${blendToTrueAngle(0.7) + overshootDeg * 0.1}deg)`,
        offset: 0.9,
        easing: 'ease-in-out',
      },
      { transform: `translateX(-50%) rotate(${trueAngleDeg}deg)`, easing: 'ease-out' },
    ];

    let introAnimationRef = null;

    const finishIntro = () => {
      // A `fill: 'forwards'` Web Animations API effect keeps holding its last keyframe's
      // transform in a cascade layer above inline styles even once `finished` resolves —
      // cancel it first, or every style.transform reset below is silently overridden.
      if (introAnimationRef) introAnimationRef.cancel();

      this.figure.classList.remove('is-intro');
      // Remove outright rather than fading: the rope is a child of `figure` anchored at
      // `top: 0` with an inline height equal to the full rope length, so once the box below
      // collapses to the mascot's own short height, a fading-but-still-tall rope would dangle
      // past the mascot instead of neatly disappearing with it.
      if (this.introRope) this.introRope.remove();

      // Jump to the mascot's actual current position, computed analytically at trueAngleDeg (the
      // rotation's own final keyframe) rather than the capped settleAngleDeg — since trueAngleDeg
      // was derived to land exactly on the target, this position should already match the resting
      // spot almost exactly, re-expressed as a normal un-rotated box in the same tick so there's
      // no flash. Same rotated-vector derivation as trueAngleDeg's own derivation above: rotating
      // the "hangs straight down" vector by θ under CSS's clockwise-positive convention gives
      // (-sinθ, cosθ), so the x component needs the same negation.
      const angleRad = trueAngleDeg * (Math.PI / 180);
      const currentCenterX = pivotX - ropeLength * Math.sin(angleRad);
      const currentTopY = pivotY + ropeLength * Math.cos(angleRad);

      this.figure.style.transform = 'none';
      this.figure.style.height = `${rect.height}px`;
      this.figure.style.left = `${currentCenterX - rect.width / 2}px`;
      this.figure.style.top = `${currentTopY}px`;

      // Re-read the resting position fresh rather than trusting the `target` captured ~1.5s ago
      // when the intro started — if the page's layout shifted since then (a lazy-loaded image,
      // for instance), the true resting spot may have moved slightly. In the common case (no
      // shift) this matches the position above almost exactly, so this transition is just a tiny
      // safety-net correction, not the main landing motion — that's now entirely the rotation.
      const freshTarget = this.getNaturalFigurePosition();

      requestAnimationFrame(() => {
        this.figure.style.transition = 'left 0.12s ease-out, top 0.12s ease-out';
        this.figure.style.left = `${freshTarget.left}px`;
        this.figure.style.top = `${freshTarget.top}px`;

        const cleanup = () => {
          this.figure.style.position = '';
          this.figure.style.left = '';
          this.figure.style.top = '';
          this.figure.style.width = '';
          this.figure.style.height = '';
          this.figure.style.transform = '';
          this.figure.style.transition = '';
          this.introPlaying = false;
          this.dispatchAnalyticsEvent('intro-end');
          this.scheduleGreeting();
          this.update();
          this.figure.removeEventListener('transitionend', cleanup);
        };
        this.figure.addEventListener('transitionend', cleanup);
        setTimeout(cleanup, 300);
      });
    };

    try {
      introAnimationRef = this.figure.animate(keyframes, {
        // Shorter than the original 1800ms — with per-keyframe easing spreading the sweep out
        // instead of front-loading it, a longer damping tail felt sluggish once the big motion
        // was actually visible throughout instead of hidden in the first quarter-second. Slightly
        // longer than the very first rebalance (1400ms) to leave room for the anticipation hold
        // above without squeezing the sweep itself back down.
        duration: 1550,
        easing: 'linear',
        fill: 'forwards',
      });
      introAnimationRef.finished.then(finishIntro).catch(finishIntro);
    } catch (error) {
      // Web Animations API unsupported or blocked — skip straight to the resting spot rather
      // than leaving the mascot stranded mid-swing
      finishIntro();
    }
  }

  playPurrSound() {
    if (!this.enableSound) return;

    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      this.audioCtx = this.audioCtx || new AudioContextClass();
      const ctx = this.audioCtx;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(380, ctx.currentTime);
      oscillator.frequency.linearRampToValueAtTime(460, ctx.currentTime + 0.25);
      gain.gain.setValueAtTime(0.06, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);

      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.35);
    } catch (error) {
      // Web Audio unsupported or blocked — fail silently
    }
  }

  playBoopSound() {
    if (!this.enableSound) return;

    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      this.audioCtx = this.audioCtx || new AudioContextClass();
      const ctx = this.audioCtx;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(520, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(760, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);

      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.15);
    } catch (error) {
      // Web Audio unsupported or blocked — fail silently, sound is a bonus not a requirement
    }
  }

  onTap() {
    if (this.dragMoved) {
      this.dragMoved = false;
      return;
    }

    this.lastActivity = Date.now();
    this.totalTaps += 1;

    const now = Date.now();
    const isDoubleTap = Boolean(this.lastTapTime) && now - this.lastTapTime < 350;
    this.lastTapTime = now;

    this.dispatchAnalyticsEvent('tap', { totalTaps: this.totalTaps, pageType: this.pageType });

    const discountCode = this.chatConfig && this.chatConfig.discountCode;
    const discountThreshold = (this.chatConfig && this.chatConfig.discountThreshold) || 5;
    const discountReady = Boolean(discountCode) && this.totalTaps >= discountThreshold && !this.wasDiscountRevealed();

    if (isDoubleTap && !discountReady) {
      // Second (or later) tap in quick succession — cancel the pending single-tap chat-open and just purr
      clearTimeout(this.singleTapTimeout);
      this.triggerPetting();
      return;
    }

    this.bounce();
    this.spawnParticles(7, 'mud');
    this.playBoopSound();

    clearTimeout(this.singleTapTimeout);

    if (discountReady) {
      this.openChat();
      this.revealDiscountInChat();
      return;
    }

    // Delay opening chat briefly in case a second tap follows — a real double-tap should only purr
    this.singleTapTimeout = setTimeout(() => {
      this.openChat();
    }, 320);
  }

  bounce() {
    this.figure.classList.remove('is-tapped');
    void this.figure.offsetWidth;
    this.figure.classList.add('is-tapped');
  }

  spawnParticles(count, variant) {
    if (!this.particleLayer) return;

    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      const distance = 24 + Math.random() * 28;
      const particle = document.createElement('div');
      particle.className = 'mud-trail-mascot__particle';

      if (variant === 'confetti') {
        particle.style.background = MUD_TRAIL_CONFETTI_COLORS[i % MUD_TRAIL_CONFETTI_COLORS.length];
        particle.style.borderRadius = '2px';
      }

      particle.style.setProperty('--mud-trail-tx', `${Math.cos(angle) * distance}px`);
      particle.style.setProperty('--mud-trail-ty', `${Math.sin(angle) * distance}px`);
      particle.style.animationDuration = `${0.5 + Math.random() * 0.3}s`;
      particle.addEventListener('animationend', () => particle.remove());
      this.particleLayer.appendChild(particle);
    }
  }

  onMouseMove(event) {
    this.lastActivity = Date.now();

    if (this.isDragging || !this.tiltLayer || this.tiltTicking) return;
    this.tiltTicking = true;

    requestAnimationFrame(() => {
      const rect = this.figure.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const delta = event.clientX - center;
      const tilt = Math.max(-10, Math.min(10, delta / 40));
      this.tiltLayer.style.setProperty('--mud-trail-tilt', `${tilt}deg`);
      this.lookAt(event.clientX, event.clientY);
      this.tiltTicking = false;
    });
  }

  onTouchLook(event) {
    const touch = event.touches[0];
    if (!touch || this.isDragging || this.lookTicking) return;
    this.lookTicking = true;
    const { clientX, clientY } = touch;
    requestAnimationFrame(() => {
      this.lookAt(clientX, clientY);
      this.lookTicking = false;
    });
  }

  /** Point both pupils at a viewport position, clamped inside the painted eyeballs */
  lookAt(x, y) {
    if (!this.eyes || this.isDragging) return;

    this.eyes.forEach(({ eye, pupil }) => {
      if (!pupil) return;
      const rect = eye.getBoundingClientRect();
      if (!rect.width) return;

      const dx = x - (rect.left + rect.width / 2);
      const dy = y - (rect.top + rect.height / 2);
      const distance = Math.hypot(dx, dy);
      const travel = Math.max(0, (rect.width - pupil.offsetWidth) / 2) * MUD_TRAIL_PUPIL_TRAVEL;
      // Softly saturating: looks straight at a nearby pointer, all the way over for a far one
      const reach = distance > 0.5 ? (distance / (distance + 40)) * travel : 0;
      const ox = distance > 0.5 ? (dx / distance) * reach : 0;
      const oy = distance > 0.5 ? (dy / distance) * reach : 0;
      pupil.style.transform = `translate(${ox.toFixed(2)}px, ${oy.toFixed(2)}px)`;
    });
  }

  startBlinking() {
    const scheduleBlink = () => {
      const delay = 3000 + Math.random() * 4000;
      this.blinkTimeout = setTimeout(() => {
        this.figure.classList.add('is-blinking');
        this.blinkResetTimeout = setTimeout(() => this.figure.classList.remove('is-blinking'), 250);
        scheduleBlink();
      }, delay);
    };
    scheduleBlink();
  }

  startIdleWatcher() {
    this.idleInterval = setInterval(() => {
      if (Date.now() - this.lastActivity > 20000) {
        this.playIdleWave();
        this.lastActivity = Date.now();
      }
    }, 5000);
  }

  playIdleWave() {
    this.figure.classList.remove('is-idle-wave');
    void this.figure.offsetWidth;
    this.figure.classList.add('is-idle-wave');
    this.idleWaveResetTimeout = setTimeout(() => this.figure.classList.remove('is-idle-wave'), 1300);
  }

  startPersonalityLoop() {
    this.personalityInterval = setInterval(() => {
      if (this.figure.classList.contains('is-walking')) return;

      const variant = MUD_TRAIL_IDLE_VARIANTS[this.idleVariantIndex % MUD_TRAIL_IDLE_VARIANTS.length];
      this.idleVariantIndex += 1;
      this.figure.classList.add(variant);
      this.idleVariantResetTimeout = setTimeout(() => this.figure.classList.remove(variant), 1500);
    }, 12000);
  }

  scheduleGreeting() {
    this.greetingTimeout = setTimeout(() => {
      if (this.hasGreeted) return;
      this.hasGreeted = true;
      this.playIdleWave();

      // Exclude the end-of-page easter egg (position >= 90) — it should never double as the arrival greeting
      const greeting = this.bubble && this.callouts.find((callout) => callout.position < 90);
      if (greeting) {
        this.showBubble(greeting.message, 4000);
        this.shownCallouts.add(greeting.position);
      }

      if (this.enableMemory) {
        try {
          if (!localStorage.getItem(MUD_TRAIL_VISITED_KEY)) {
            localStorage.setItem(MUD_TRAIL_VISITED_KEY, '1');
          }
        } catch (error) {
          // Storage unavailable — every visit is treated as a first visit, which is a harmless fallback
        }
      }
    }, 1800);
  }

  isReturningVisitor() {
    try {
      return localStorage.getItem(MUD_TRAIL_VISITED_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  applyMemory() {
    if (this.pageType === 'index' && this.isReturningVisitor()) {
      const remembered = this.getRememberedProduct();
      if (remembered) {
        // Insert as a new low-position entry rather than mutating an existing one — if no callout
        // blocks are configured, callouts[0] could be the end-of-page easter egg at position 96
        this.callouts.push({ message: `Still thinking about ${remembered.title}?`, position: 5 });
        this.rememberedProduct = remembered;
      }
    }
  }

  getRememberedProduct() {
    try {
      const raw = localStorage.getItem(MUD_TRAIL_MEMORY_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      return data && data.title && data.url ? data : null;
    } catch (error) {
      return null;
    }
  }

  onCartChange(event) {
    if (event.detail && event.detail.cart && typeof event.detail.cart.item_count === 'number') {
      this.cartItemCount = event.detail.cart.item_count;
    }
    if (event.detail && event.detail.baseEvent === 'variant:add') {
      this.celebrate();
    }
  }

  celebrate() {
    this.bounce();
    this.spawnParticles(18, 'confetti');
    this.showBubble('Yay! Great choice! 🎉', 4000);
    this.dispatchAnalyticsEvent('add-to-cart-celebration');
  }

  onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.wasHidden = true;
      this.hiddenAt = Date.now();
      return;
    }

    if (document.visibilityState === 'visible' && this.wasHidden) {
      this.wasHidden = false;
      if (Date.now() - (this.hiddenAt || 0) > 5000) {
        this.bounce();
        this.showBubble("Oh, you're back! 👋", 3000);
      }
    }
  }

  onMouseLeaveDoc(event) {
    if (this.hasShownExitIntent || event.clientY > 0) return;

    this.hasShownExitIntent = true;
    document.removeEventListener('mouseleave', this.onMouseLeaveDoc);
    this.bounce();
    this.showBubble(this.exitMessage, 6000);
    this.dispatchAnalyticsEvent('exit-intent-shown');
  }

  wasDiscountRevealed() {
    try {
      return sessionStorage.getItem(MUD_TRAIL_DISCOUNT_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  dispatchAnalyticsEvent(name, detail = {}) {
    if (!this.enableAnalytics) return;

    const eventName = `mud-trail:${name}`;
    document.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail }));

    if (window.dataLayer) {
      window.dataLayer.push({ event: eventName, ...detail });
    }
  }

  /* ---------------------------------------------------------------------
   * Chat assistant
   * ------------------------------------------------------------------- */

  onOutsideClick(event) {
    if (!this.chatOpen || !this.chatEl) return;
    if (this.chatEl.contains(event.target) || this.figure.contains(event.target)) return;
    this.closeChat();
  }

  initChat() {
    const fragment = this.chatTemplate.content.cloneNode(true);
    this.chatEl = fragment.firstElementChild;
    this.appendChild(this.chatEl);

    this.chatMessagesEl = this.chatEl.querySelector('[data-mud-trail-chat-messages]');
    this.chatRepliesEl = this.chatEl.querySelector('[data-mud-trail-chat-replies]');
    this.chatInputEl = this.chatEl.querySelector('[data-mud-trail-chat-input]');
    this.chatFormEl = this.chatEl.querySelector('[data-mud-trail-chat-form]');

    const closeButton = this.chatEl.querySelector('[data-mud-trail-chat-close]');
    if (closeButton) closeButton.addEventListener('click', () => this.closeChat());

    if (this.chatFormEl) {
      this.chatFormEl.addEventListener('submit', (event) => {
        event.preventDefault();
        const text = this.chatInputEl.value.trim();
        if (!text) return;
        this.chatInputEl.value = '';
        this.addUserMessage(text);
        this.routeIntent(text);
      });
    }

    if (this.restoreChatHistory()) {
      this.chatGreeted = true;

      if (this.chatConfig && this.chatConfig.product && this.chatConfig.product.handle) {
        this.addBotMessage("Oh, and I see you're checking this one out:", false);
        this.startBuyFlow(this.chatConfig.product.handle, 1);
      }

      this.showMainMenu();
    }
  }

  saveChatHistoryEntry(sender, text) {
    try {
      const raw = sessionStorage.getItem(MUD_TRAIL_CHAT_HISTORY_KEY);
      const history = raw ? JSON.parse(raw) : [];
      history.push({ sender, text });
      sessionStorage.setItem(MUD_TRAIL_CHAT_HISTORY_KEY, JSON.stringify(history.slice(-20)));
    } catch (error) {
      // Storage unavailable — the conversation just won't persist across pages, which is an acceptable fallback
    }
  }

  restoreChatHistory() {
    try {
      const raw = sessionStorage.getItem(MUD_TRAIL_CHAT_HISTORY_KEY);
      if (!raw) return false;

      const history = JSON.parse(raw);
      if (!Array.isArray(history) || !history.length) return false;

      history.forEach((entry) => {
        if (!entry || typeof entry.text !== 'string') return;
        if (entry.sender === 'user') {
          this.addUserMessage(entry.text, false);
        } else {
          this.addBotMessage(entry.text, false);
        }
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  openChat() {
    if (!this.chatTemplate) return;

    if (!this.chatEl) {
      this.initChat();
    }

    this.chatEl.classList.add('is-visible');
    this.chatOpen = true;
    document.addEventListener('click', this.onOutsideClick, true);
    this.playPurrSound();

    if (!this.chatGreeted) {
      this.chatGreeted = true;
      this.dispatchAnalyticsEvent('chat-open', { pageType: this.pageType });
      window.setTimeout(() => this.sendGreeting(), 300);
    } else {
      this.focusChatInput();
    }
  }

  closeChat() {
    if (this.chatEl) this.chatEl.classList.remove('is-visible');
    this.chatOpen = false;
  }

  focusChatInput() {
    if (this.chatInputEl) {
      window.setTimeout(() => this.chatInputEl.focus(), 50);
    }
  }

  sendGreeting() {
    const config = this.chatConfig;
    if (!config) return;

    this.addBotMessage(config.greeting || 'Hi there! How can I help?');

    if (config.product && config.product.handle) {
      this.startBuyFlow(config.product.handle, 1);
    } else if (config.ctaType === 'link' && config.ctaUrl && config.ctaLabel) {
      this.addQuickReplies([{ label: config.ctaLabel, action: () => { window.location.href = config.ctaUrl; } }]);
    }

    this.showMainMenu();
  }

  showMainMenu() {
    const options = [
      { label: '🔍 Find a product', action: () => this.promptProductSearch() },
      { label: '📦 Track my order', action: () => this.trackOrder() },
      { label: '🚚 Shipping & returns', action: () => this.showShippingReturnsMenu() },
      { label: '📏 Sizing help', action: () => this.faqAnswer('sizing') },
      { label: '💳 Payment methods', action: () => this.faqAnswer('payment') },
      { label: 'ℹ️ More info', action: () => this.showMoreInfoMenu() },
      { label: '💬 Talk to a human', action: () => this.contactHuman() },
    ];

    if (this.rememberedProduct) {
      const remembered = this.rememberedProduct;
      options.unshift({
        label: `👀 Still thinking about ${remembered.title}?`,
        action: () => {
          if (remembered.handle) {
            this.startBuyFlow(remembered.handle, 1);
          } else {
            window.location.href = remembered.url;
          }
        },
      });
    }

    this.addQuickReplies(options);
  }

  showShippingReturnsMenu() {
    this.addQuickReplies([
      { label: 'Shipping info', action: () => this.faqAnswer('shipping') },
      { label: 'Returns & exchanges', action: () => this.faqAnswer('returns') },
      { label: 'International shipping', action: () => this.faqAnswer('international') },
      { label: '⬅ Main menu', action: () => this.showMainMenu() },
    ]);
  }

  showMoreInfoMenu() {
    this.addBotMessage("What would you like to know?");
    this.addQuickReplies([
      { label: '🎁 Gift wrapping', action: () => this.faqAnswer('gifts') },
      { label: '📦 Bulk / wholesale', action: () => this.faqAnswer('bulk') },
      { label: '✏️ Cancel or change an order', action: () => this.faqAnswer('cancel') },
      { label: '⬅ Main menu', action: () => this.showMainMenu() },
    ]);
  }

  faqAnswer(topic) {
    const text = this.chatConfig && this.chatConfig.faq && this.chatConfig.faq[topic];
    this.addBotMessage(text || "I don't have an answer for that yet — try reaching out on WhatsApp!");
    this.addQuickReplies([{ label: '⬅ Main menu', action: () => this.showMainMenu() }]);
    this.dispatchAnalyticsEvent('faq-viewed', { topic });
  }

  trackOrder() {
    const config = this.chatConfig;
    if (!config) return;

    if (config.customerLoggedIn) {
      this.addBotMessage('You can see all your order statuses in your account.');
      this.addQuickReplies([
        { label: 'View my orders', action: () => { window.location.href = config.accountUrl; } },
        { label: '⬅ Main menu', action: () => this.showMainMenu() },
      ]);
    } else {
      this.addBotMessage("Log in to your account to check your order status, or message us and we'll look it up for you.");
      this.addQuickReplies([
        { label: 'Log in', action: () => { window.location.href = config.accountLoginUrl; } },
        { label: 'Message us', action: () => this.contactHuman() },
        { label: '⬅ Main menu', action: () => this.showMainMenu() },
      ]);
    }
  }

  contactHuman() {
    const config = this.chatConfig;
    if (!config) return;

    this.addBotMessage('You can reach our team here:');
    const options = [];

    if (config.whatsappNumber) {
      const digits = config.whatsappNumber.replace(/[^\d]/g, '');
      options.push({ label: '💬 WhatsApp us', action: () => window.open(`https://wa.me/${digits}`, '_blank') });
    }

    if (config.instagramHandle) {
      const handle = config.instagramHandle.replace(/^@/, '').trim();
      options.push({ label: '📸 Instagram', action: () => window.open(`https://instagram.com/${handle}`, '_blank') });
    }

    options.push({ label: '✉️ Contact page', action: () => { window.location.href = config.contactUrl; } });
    options.push({ label: '⬅ Main menu', action: () => this.showMainMenu() });

    this.addQuickReplies(options);
  }

  promptProductSearch() {
    this.addBotMessage("What are you looking for? You can even tell me a quantity, like \"2 eggcellent tee\".");
    this.focusChatInput();
  }

  routeIntent(text) {
    const lower = text.toLowerCase().trim();

    if (/^(hi+|hey+|hello+|hola|yo|salam+|assalam.*alaikum|good\s*morning|good\s*afternoon|good\s*evening)\b/.test(lower)) {
      const greetings = [
        "Hey there! 👋 So happy you're here — what can I help you with?",
        'Hi hi! 🐾 What can I find for you today?',
        "Hello! I'm all ears — ask me anything!",
      ];
      this.addBotMessage(greetings[Math.floor(Math.random() * greetings.length)]);
      return this.showMainMenu();
    }

    if (/\bhow are you\b/.test(lower)) {
      this.addBotMessage("I'm doing muddy-tastic, thanks for asking! 😄 How can I help you today?");
      return this.showMainMenu();
    }

    if (/\b(how can you help|what can you do|what do you do|can you help)\b/.test(lower)) {
      this.addBotMessage(
        'I can help you find products, add things to your cart, check shipping/sizing/payment info, or connect you with our team. Just ask, or pick an option below!'
      );
      return this.showMainMenu();
    }

    if (/\b(thank you|thanks|thankyou|shukriya)\b/.test(lower)) {
      this.addBotMessage("You're so welcome! 💕 Anything else I can help with?");
      return this.showMainMenu();
    }

    if (/^(bye|goodbye|see you|khuda hafiz|allah hafiz)\b/.test(lower)) {
      this.addBotMessage('Bye for now! Come back and pet me anytime 🐾');
      return;
    }

    if (/\b(what('s| is) your name|who are you)\b/.test(lower)) {
      this.addBotMessage("I'm Sprout, your Luv Mud shopping buddy! 🌱 I can help you find things, add them to your cart, or answer questions.");
      return this.showMainMenu();
    }

    if (/\b(are you (a )?(real|human|robot|bot|ai)|are you real)\b/.test(lower)) {
      this.addBotMessage(
        "I'm a little bit of code and a lot of heart — not a real person, but I'll do my best to help! Want an actual human instead?"
      );
      return this.addQuickReplies([
        { label: '💬 Talk to a human', action: () => this.contactHuman() },
        { label: '⬅ Main menu', action: () => this.showMainMenu() },
      ]);
    }

    if (/\b(tell me a joke|joke|make me laugh)\b/.test(lower)) {
      const jokes = [
        "Why did the sock cross the road? To get to the other pair! 🧦",
        "What do you call a baby who won't nap? A little muddler. 😄",
        "Why was the toddler's t-shirt sad? It felt a little wrinkled up inside. 👕",
      ];
      this.addBotMessage(jokes[Math.floor(Math.random() * jokes.length)]);
      return this.showMainMenu();
    }

    if (/\b(you'?re (so )?(cute|adorable|sweet|the best)|i love (you|this))\b/.test(lower)) {
      this.addBotMessage("Aww, you're making me blush! 🥰 Anything I can help you find?");
      return this.showMainMenu();
    }

    if (/\b(shut up|go away|stop it|annoying|leave me alone)\b/.test(lower)) {
      this.addBotMessage("Sorry to bother you! I'll be quiet — tap me anytime if you need help. 💜");
      return;
    }

    if (/\b(checkout|check out|complete my order|pay now)\b/.test(lower)) {
      if (this.cartItemCount > 0) {
        this.addBotMessage("Let's get you to checkout!");
        return this.addQuickReplies([
          { label: 'Go to checkout', action: () => { window.location.href = this.chatConfig.checkoutUrl; } },
          { label: '⬅ Main menu', action: () => this.showMainMenu() },
        ]);
      }
      this.addBotMessage("Your cart's empty right now — want help finding something first?");
      return this.showMainMenu();
    }

    if (/\b(shipping|delivery|deliver)\b/.test(lower)) return this.faqAnswer('shipping');
    if (/\b(return|exchange|refund)\b/.test(lower)) return this.faqAnswer('returns');
    if (/\b(size|sizing|fit)\b/.test(lower)) return this.faqAnswer('sizing');
    if (/\b(payment|cod|cash on delivery|card|pay)\b/.test(lower)) return this.faqAnswer('payment');
    if (/\b(gift|wrap|wrapping)\b/.test(lower)) return this.faqAnswer('gifts');
    if (/\b(bulk|wholesale)\b/.test(lower)) return this.faqAnswer('bulk');
    if (/\b(cancel|change my order|modify (my )?order)\b/.test(lower)) return this.faqAnswer('cancel');
    if (/\b(international|abroad|outside pakistan|worldwide|overseas)\b/.test(lower)) return this.faqAnswer('international');
    if (/\b(track|order status|my order)\b/.test(lower)) return this.trackOrder();
    if (/\b(human|agent|whatsapp|instagram|contact|support)\b/.test(lower)) return this.contactHuman();
    if (/\b(menu|options|help)\b/.test(lower) && lower.length < 20) {
      this.addBotMessage("Here's what I can help with:");
      return this.showMainMenu();
    }

    this.handleProductQuery(text);
  }

  handleProductQuery(rawText) {
    const match = rawText.match(/^\s*(?:i\s*want\s*|i'd\s*like\s*|add\s*|buy\s*|get\s*)?(\d+)?\s*(?:x\s*|of\s*)?(.+)$/i);
    const quantity = match && match[1] ? Math.max(1, parseInt(match[1], 10)) : 1;
    const query = (match && match[2] ? match[2] : rawText).trim();

    if (!query) {
      this.addBotMessage("Tell me what you're looking for and I'll find it!");
      return;
    }

    this.searchProducts(query, quantity);
  }

  async searchProducts(query, quantity = 1) {
    this.showTyping();

    try {
      const url = `${Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last`;
      const response = await fetch(url);
      const data = await response.json();
      this.hideTyping();

      const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];

      if (!products.length) {
        this.addBotMessage(`I couldn't find anything matching "${query}". Want to try a different search, or browse everything?`);
        this.addQuickReplies([
          { label: 'Browse all products', action: () => { window.location.href = this.chatConfig.allProductsUrl; } },
          { label: '⬅ Main menu', action: () => this.showMainMenu() },
        ]);
        return;
      }

      if (products.length === 1) {
        this.startBuyFlow(this.getProductHandle(products[0]), quantity);
        return;
      }

      this.addBotMessage(`I found a few things for "${query}" — which one?`);
      products.slice(0, 4).forEach((product) => {
        this.renderSearchResultCard(product, quantity);
      });
    } catch (error) {
      this.hideTyping();
      this.addBotMessage("Hmm, I couldn't search right now. Please try again in a moment.");
    }
  }

  renderSearchResultCard(product, quantity) {
    const card = document.createElement('div');
    card.className = 'mud-trail-mascot__chat-product-card';

    const imageUrl = (product.featured_image && product.featured_image.url) || product.image || '';

    const body = document.createElement('div');
    body.className = 'mud-trail-mascot__chat-product-body';

    if (imageUrl) {
      const img = document.createElement('img');
      img.className = 'mud-trail-mascot__chat-product-image';
      img.src = this.toImageUrl(imageUrl, 100);
      img.alt = '';
      img.loading = 'lazy';
      body.appendChild(img);
    }

    const textWrap = document.createElement('div');
    const title = document.createElement('p');
    title.className = 'mud-trail-mascot__chat-product-title';
    title.textContent = product.title;
    const price = document.createElement('p');
    price.className = 'mud-trail-mascot__chat-product-price';
    price.textContent = product.price || '';
    textWrap.append(title, price);
    body.appendChild(textWrap);
    card.appendChild(body);

    const actions = document.createElement('div');
    actions.className = 'mud-trail-mascot__chat-product-actions';

    const viewLink = document.createElement('a');
    viewLink.href = product.url;
    viewLink.className = 'button button--sm';
    viewLink.textContent = 'View';
    actions.appendChild(viewLink);

    const selectButton = document.createElement('button');
    selectButton.type = 'button';
    selectButton.className = 'button button--sm';
    selectButton.textContent = quantity > 1 ? `Select (${quantity})` : 'Select';
    selectButton.addEventListener('click', () => this.startBuyFlow(this.getProductHandle(product), quantity));
    actions.appendChild(selectButton);

    card.appendChild(actions);
    this.chatMessagesEl.appendChild(card);
    this.scrollChatToBottom();
  }

  async startBuyFlow(handle, quantity = 1) {
    this.showTyping();

    try {
      const response = await fetch(`${Shopify.routes.root}products/${handle}.js`);
      if (!response.ok) throw new Error('Product not found');
      const productJson = await response.json();
      this.hideTyping();
      this.addBotMessage(`Here's ${productJson.title}:`);
      this.renderBuyCard(productJson, quantity);
    } catch (error) {
      this.hideTyping();
      this.addBotMessage("Sorry, I couldn't load that product. Want to try searching again?");
      this.showMainMenu();
    }
  }

  renderBuyCard(productJson, initialQuantity) {
    const card = document.createElement('div');
    card.className = 'mud-trail-mascot__chat-product-card';

    let selectedVariant = productJson.variants.find((variant) => variant.available) || productJson.variants[0];
    let quantity = Math.max(1, initialQuantity || 1);

    const imageUrl = productJson.featured_image || (productJson.images && productJson.images[0]) || '';

    const body = document.createElement('div');
    body.className = 'mud-trail-mascot__chat-product-body';

    if (imageUrl) {
      const img = document.createElement('img');
      img.className = 'mud-trail-mascot__chat-product-image';
      img.src = this.toImageUrl(imageUrl, 100);
      img.alt = '';
      img.loading = 'lazy';
      body.appendChild(img);
    }

    const textWrap = document.createElement('div');
    const title = document.createElement('p');
    title.className = 'mud-trail-mascot__chat-product-title';
    title.textContent = productJson.title;
    const price = document.createElement('p');
    price.className = 'mud-trail-mascot__chat-product-price';
    textWrap.append(title, price);
    body.appendChild(textWrap);
    card.appendChild(body);

    const updatePrice = () => {
      price.textContent = this.formatMoney(selectedVariant.price);
      if (selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price) {
        const strike = document.createElement('s');
        strike.textContent = this.formatMoney(selectedVariant.compare_at_price);
        price.appendChild(strike);
      }
    };
    updatePrice();

    if (productJson.variants.length > 1) {
      const select = document.createElement('select');
      select.className = 'mud-trail-mascot__chat-variant-select';
      productJson.variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.available ? variant.title : `${variant.title} (sold out)`;
        option.disabled = !variant.available;
        select.appendChild(option);
      });
      select.value = selectedVariant.id;
      select.addEventListener('change', () => {
        selectedVariant = productJson.variants.find((variant) => String(variant.id) === select.value);
        updatePrice();
      });
      card.appendChild(select);
    }

    const controls = document.createElement('div');
    controls.className = 'mud-trail-mascot__chat-product-controls';

    const qtyWrap = document.createElement('div');
    qtyWrap.className = 'mud-trail-mascot__chat-qty';

    const minusButton = document.createElement('button');
    minusButton.type = 'button';
    minusButton.textContent = '−';
    minusButton.setAttribute('aria-label', 'Decrease quantity');

    const qtyValue = document.createElement('span');
    qtyValue.className = 'mud-trail-mascot__chat-qty-value';
    qtyValue.textContent = String(quantity);

    const plusButton = document.createElement('button');
    plusButton.type = 'button';
    plusButton.textContent = '+';
    plusButton.setAttribute('aria-label', 'Increase quantity');

    minusButton.addEventListener('click', () => {
      quantity = Math.max(1, quantity - 1);
      qtyValue.textContent = String(quantity);
    });
    plusButton.addEventListener('click', () => {
      quantity += 1;
      qtyValue.textContent = String(quantity);
    });

    qtyWrap.append(minusButton, qtyValue, plusButton);

    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'button button--sm';
    addButton.textContent = 'Add to cart';
    addButton.addEventListener('click', async () => {
      if (!selectedVariant.available) {
        this.addBotMessage('That option is sold out — try picking a different one!');
        return;
      }

      const requestedQuantity = quantity;
      const requestedVariantId = selectedVariant.id;

      addButton.disabled = true;
      addButton.textContent = 'Adding...';
      minusButton.disabled = true;
      plusButton.disabled = true;

      const ok = await this.addVariantToCart(requestedVariantId, requestedQuantity);

      if (ok) {
        addButton.textContent = 'Added! ✓';
        this.addBotMessage(`Added ${requestedQuantity} × ${productJson.title} to your cart! 🎉`);
        this.suggestComplement(productJson.id);
      } else {
        addButton.disabled = false;
        addButton.textContent = 'Add to cart';
        minusButton.disabled = false;
        plusButton.disabled = false;
        this.addBotMessage('Something went wrong adding that to your cart. Please try again.');
      }
    });

    controls.append(qtyWrap, addButton);
    card.appendChild(controls);

    this.chatMessagesEl.appendChild(card);
    this.scrollChatToBottom();
  }

  async addVariantToCart(variantId, quantity) {
    try {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const formData = new FormData();
      formData.set('id', variantId);
      formData.set('quantity', quantity);
      formData.set('sections', sectionsToBundle.join(','));
      formData.set('sections_url', `${Shopify.routes.root}variants/${variantId}`);

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      const responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

      if (!response.ok) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return false;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart: cartContent },
        })
      );

      return true;
    } catch (error) {
      return false;
    }
  }

  async suggestComplement(productId) {
    if (!this.chatConfig || !this.chatConfig.enableRecommendations) return;

    try {
      const response = await fetch(`${Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=1&intent=complementary`);
      if (!response.ok) return;

      const data = await response.json();
      const rec = data.products && data.products[0];
      if (!rec) return;

      this.addBotMessage(`This goes great with ${rec.title}! Want to see it?`);
      this.addQuickReplies([
        { label: `View ${rec.title}`, action: () => { window.location.href = rec.url; } },
        { label: 'No thanks', action: () => this.showMainMenu() },
      ]);
    } catch (error) {
      // Recommendations are a nice-to-have — silently skip on failure
    }
  }

  revealDiscountInChat() {
    try {
      sessionStorage.setItem(MUD_TRAIL_DISCOUNT_KEY, '1');
    } catch (error) {
      // Storage unavailable — the reveal may repeat on tap-threshold, which is an acceptable fallback
    }

    this.spawnParticles(20, 'confetti');
    this.addBotMessage(`🎉 ${this.chatConfig.discountIntro || 'You found a surprise!'}`);

    const codeBlock = document.createElement('div');
    codeBlock.className = 'mud-trail-mascot__chat-discount-code';
    codeBlock.textContent = this.chatConfig.discountCode;
    this.chatMessagesEl.appendChild(codeBlock);

    const copyButton = document.createElement('button');
    copyButton.type = 'button';
    copyButton.className = 'button button--sm';
    copyButton.textContent = 'Copy code';
    copyButton.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(this.chatConfig.discountCode);
        copyButton.textContent = 'Copied!';
        setTimeout(() => {
          copyButton.textContent = 'Copy code';
        }, 2000);
      } catch (error) {
        // Clipboard unavailable — the code is still visible on the card to copy manually
      }
    });
    this.chatMessagesEl.appendChild(copyButton);
    this.scrollChatToBottom();
    this.dispatchAnalyticsEvent('discount-revealed');
  }

  addBotMessage(text, persist = true) {
    if (!this.chatMessagesEl) return;
    const bubble = document.createElement('div');
    bubble.className = 'mud-trail-mascot__chat-bubble mud-trail-mascot__chat-bubble--bot';
    bubble.textContent = text;
    this.chatMessagesEl.appendChild(bubble);
    this.scrollChatToBottom();
    if (persist) this.saveChatHistoryEntry('bot', text);
  }

  addUserMessage(text, persist = true) {
    if (!this.chatMessagesEl) return;
    const bubble = document.createElement('div');
    bubble.className = 'mud-trail-mascot__chat-bubble mud-trail-mascot__chat-bubble--user';
    bubble.textContent = text;
    this.chatMessagesEl.appendChild(bubble);
    this.scrollChatToBottom();
    if (persist) this.saveChatHistoryEntry('user', text);
  }

  addQuickReplies(options) {
    if (!this.chatRepliesEl) return;
    this.chatRepliesEl.innerHTML = '';

    options.forEach((option) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'mud-trail-mascot__chat-reply-chip';
      chip.textContent = option.label;
      chip.addEventListener('click', () => {
        this.addUserMessage(option.label);
        this.chatRepliesEl.innerHTML = '';
        option.action();
      });
      this.chatRepliesEl.appendChild(chip);
    });
  }

  showTyping() {
    if (this.typingEl || !this.chatMessagesEl) return;
    this.typingEl = document.createElement('div');
    this.typingEl.className = 'mud-trail-mascot__chat-typing';
    this.typingEl.innerHTML = '<span></span><span></span><span></span>';
    this.chatMessagesEl.appendChild(this.typingEl);
    this.scrollChatToBottom();
  }

  hideTyping() {
    if (this.typingEl) {
      this.typingEl.remove();
      this.typingEl = null;
    }
  }

  scrollChatToBottom() {
    if (this.chatMessagesEl) {
      this.chatMessagesEl.scrollTop = this.chatMessagesEl.scrollHeight;
    }
  }

  toImageUrl(url, width) {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  getProductHandle(product) {
    if (product.handle) return product.handle;
    try {
      const path = new URL(product.url, window.location.origin).pathname;
      const parts = path.split('/').filter(Boolean);
      return parts[parts.length - 1];
    } catch (error) {
      return '';
    }
  }

  formatMoney(cents) {
    const safeCents = Number.isFinite(cents) ? cents : 0;
    const amount = (Math.max(0, safeCents) / 100).toFixed(2);
    const [whole, decimal] = amount.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `Rs.${withCommas}.${decimal}`;
  }
}

if (!window.customElements.get('mud-trail-mascot')) {
  window.customElements.define('mud-trail-mascot', MudTrailMascot);
}
